import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { SearchInput } from '@/shared/ui/SearchInput';
import {
  resolveCategoryIcon,
  searchCategoryIcons,
  DEFAULT_CATEGORY_ICON,
} from '../lib/categoryIcons';

export interface IconPickerProps {
  /** Currently selected lucide icon name, or null for the default. */
  value: string | null;
  onChange: (icon: string | null) => void;
  disabled?: boolean;
  /** Accessible label — the trigger is icon-only. */
  ariaLabel?: string;
}

/**
 * Searchable icon chooser for a category.
 *
 * A popover grid rather than a `Select`: the point is to SEE the glyphs, and a
 * dropdown of sixty text rows named "MousePointerClick" would defeat that.
 * Search matches spaced words too ("shopping cart" finds `ShoppingCart`), since
 * nobody types PascalCase.
 */
export function IconPicker({ value, onChange, disabled, ariaLabel }: IconPickerProps) {
  const { t } = useTranslation('knowledge');
  const triggerLabel = ariaLabel ?? t('taxonomy.iconPicker.chooseIcon');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const results = useMemo(() => searchCategoryIcons(query), [query]);
  const Selected = resolveCategoryIcon(value);

  const choose = (name: string | null) => {
    onChange(name);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          aria-label={triggerLabel}
          className="size-9 shrink-0 p-0"
        >
          <Selected className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <SearchInput
          // eslint-disable-next-line jsx-a11y/no-autofocus -- search field of a popover the user just opened
          autoFocus
          size="sm"
          value={query}
          onChange={setQuery}
          placeholder={t('taxonomy.iconPicker.searchIcons')}
          aria-label={t('taxonomy.iconPicker.searchIconsLabel')}
          className="mb-2"
        />

        {results.length === 0 ? (
          <p className="px-1 py-6 text-center text-xs text-muted-foreground">
            {t('taxonomy.iconPicker.noIconMatches', { query: query.trim() })}
          </p>
        ) : (
          <div className="grid max-h-56 grid-cols-8 gap-1 overflow-y-auto p-1">
            {results.map((name) => {
              const Icon = resolveCategoryIcon(name);
              const active = name === value;
              return (
                <button
                  key={name}
                  type="button"
                  title={name}
                  aria-label={name}
                  aria-pressed={active}
                  onClick={() => choose(name)}
                  className={`flex size-7 items-center justify-center rounded-md transition-colors ${
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }`}
                >
                  <Icon className="size-4" />
                </button>
              );
            })}
          </div>
        )}

        {value && (
          <div className="mt-2 border-t border-border-light pt-2">
            <Button variant="ghost" size="sm" className="w-full" onClick={() => choose(null)}>
              <DEFAULT_CATEGORY_ICON className="size-4" />
              {t('taxonomy.iconPicker.useDefault')}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

IconPicker.displayName = 'IconPicker';
